import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CatalogListCardRecord } from '../catalog/catalog-api';
import { useCatalogApi } from '../catalog/catalog-api';
import {
  useVersionedImageReview,
  VersionedImageReviewStatus,
} from '../catalog/useVersionedImageReview';
import { useStorageApi } from '../storage/storage-api';
import {
  EMPTY_REVIEW_IMAGE_ROTATIONS,
  MagnifiableCardImage,
  ReviewImageGridAction,
  ReviewImageRotationEditor,
  type ReviewImageRotations,
  type ReviewImageSide,
} from './BatchIdentificationWorkbenchPage';
import { rotateReviewImageToJpeg } from './review-image-rotation';
import { OwnershipLabel, useOwnershipLabels } from '../consignment/OwnershipLabel';

function decodeRouteId(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function preliminaryImageReviewLabel(card: CatalogListCardRecord): string {
  if (card.pendingImageReplacement !== undefined) return 'Image replacement awaiting review';
  if (card.imageQualityStatus === 'REJECTED') return 'Identity review: image issue';
  if (card.imageQualityStatus === 'ACCEPTED') {
    return 'Final images accepted';
  }
  if (card.preliminaryImageReview?.status === 'ISSUE') return 'Image review: issue noted';
  if (card.preliminaryImageReview?.status === 'APPROVED') return 'Image review: approved';
  return 'Image review: not completed';
}

export type ImageReviewDisposition = 'approved' | 'issue' | 'pending';

export function imageReviewDisposition(card: CatalogListCardRecord): ImageReviewDisposition {
  if (card.pendingImageReplacement !== undefined || card.imageQualityStatus === 'REJECTED')
    return 'issue';
  if (card.imageQualityStatus === 'ACCEPTED') {
    return 'approved';
  }
  if (card.preliminaryImageReview?.status === 'ISSUE') return 'issue';
  if (card.preliminaryImageReview?.status === 'APPROVED') return 'approved';
  return 'pending';
}

export function imageReviewIsComplete(card: CatalogListCardRecord): boolean {
  return imageReviewDisposition(card) !== 'pending';
}

function imageReviewDispositionLabel(disposition: ImageReviewDisposition): string {
  if (disposition === 'approved') return 'Approved';
  if (disposition === 'issue') return 'Issue';
  return 'Not reviewed';
}

type CorrectionPreview = {
  cardId: string;
  urls: Partial<Record<ReviewImageSide, string>>;
};

const IMAGE_REVIEW_WIDTH_STORAGE_KEY = 'tcvs:image-review-workspace-width';

function restoreImageReviewWidth(element: HTMLDivElement): void {
  try {
    const storedWidth = Number.parseFloat(
      window.sessionStorage.getItem(IMAGE_REVIEW_WIDTH_STORAGE_KEY) ?? '',
    );
    if (!Number.isFinite(storedWidth) || storedWidth <= 0) return;
    const parentWidth = element.parentElement?.clientWidth ?? 0;
    const availableWidth = parentWidth > 0 ? parentWidth : storedWidth;
    element.style.width = `${Math.min(storedWidth, availableWidth)}px`;
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function persistImageReviewWidth(width: number): void {
  if (!Number.isFinite(width) || width <= 0) return;
  try {
    window.sessionStorage.setItem(IMAGE_REVIEW_WIDTH_STORAGE_KEY, String(Math.round(width)));
  } catch {
    // Native resizing remains usable even when browser storage is unavailable.
  }
}

export function BatchImageReviewPage(): JSX.Element {
  const rawId = useParams<{ id: string }>().id ?? '';
  const batchId = decodeRouteId(rawId);
  const [searchParams] = useSearchParams();
  const requestedCardId = searchParams.get('card')?.trim() ?? '';
  const catalog = useCatalogApi();
  const storage = useStorageApi();
  const navigate = useNavigate();
  const [cards, setCards] = useState<CatalogListCardRecord[] | null>(null);
  const ownership = useOwnershipLabels((cards ?? []).map((card) => card.cardId));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [rotations, setRotations] = useState<ReviewImageRotations>(EMPTY_REVIEW_IMAGE_ROTATIONS);
  const [grid, setGrid] = useState(true);
  const [imageIssue, setImageIssue] = useState(false);
  const [busySide, setBusySide] = useState<ReviewImageSide | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [correctionPreview, setCorrectionPreview] = useState<CorrectionPreview | null>(null);
  const correctionPreviewRef = useRef<CorrectionPreview | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<boolean> => {
    if (batchId === '') return false;
    const result = await catalog.list({ placementBatchId: batchId, limit: 500 });
    if (!result.ok) {
      setError(result.message ?? result.error);
      setLoadingBatch(false);
      return false;
    }
    const items = result.data.items;
    setCards(items);
    setActiveId((current) => {
      if (current !== null && items.some((card) => card.cardId === current)) return current;
      const requested = items.find((card) => card.cardId === requestedCardId);
      const firstPending = items.find((card) => !imageReviewIsComplete(card));
      return requested?.cardId ?? firstPending?.cardId ?? items[0]?.cardId ?? null;
    });
    setError(null);
    setLoadingBatch(false);
    return true;
  }, [batchId, catalog, requestedCardId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeIndex = useMemo(
    () => cards?.findIndex((card) => card.cardId === activeId) ?? -1,
    [activeId, cards],
  );
  const sourceCard = activeIndex < 0 ? null : (cards?.[activeIndex] ?? null);
  const photoReview = useVersionedImageReview(sourceCard, catalog, 'PRELIMINARY');
  const active = photoReview.card;
  const reviewedCount = cards?.filter(imageReviewIsComplete).length ?? 0;

  useEffect(() => {
    setRotations(EMPTY_REVIEW_IMAGE_ROTATIONS);
    setError(null);
  }, [active?.cardId]);

  useEffect(() => {
    setImageIssue(active?.preliminaryImageReview?.status === 'ISSUE');
  }, [active?.cardId, active?.preliminaryImageReview?.status]);

  useEffect(
    () => () => {
      for (const url of Object.values(correctionPreviewRef.current?.urls ?? {})) {
        URL.revokeObjectURL(url);
      }
    },
    [],
  );

  const clearCorrectionPreview = useCallback(() => {
    const current = correctionPreviewRef.current;
    if (current === null) return;
    correctionPreviewRef.current = null;
    setCorrectionPreview(null);
    window.setTimeout(() => {
      for (const url of Object.values(current.urls)) URL.revokeObjectURL(url);
    }, 0);
  }, []);

  const showCorrectionPreview = (cardId: string, side: ReviewImageSide, blob: Blob) => {
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(blob);
    const current = correctionPreviewRef.current;
    const previousSideUrl = current?.cardId === cardId ? current.urls[side] : undefined;
    const next: CorrectionPreview = {
      cardId,
      urls: {
        ...(current?.cardId === cardId ? current.urls : {}),
        [side]: url,
      },
    };
    correctionPreviewRef.current = next;
    setCorrectionPreview(next);
    setRotations((existing) => ({ ...existing, [side]: 0 }));
    if (previousSideUrl !== undefined) {
      window.setTimeout(() => URL.revokeObjectURL(previousSideUrl), 0);
    }
  };

  const moveTo = (index: number) => {
    const card = cards?.[index];
    if (card === undefined || saving || loadingBatch || busySide !== null) return;
    clearCorrectionPreview();
    setNotice(null);
    setActiveId(card.cardId);
  };

  const moveToNextBatch = useCallback(async (): Promise<void> => {
    let page: string | undefined;
    do {
      const result = await storage.listContainers('batches', page, {
        includeStatus: true,
        limit: 25,
      });
      if (!result.ok) throw new Error(result.message ?? result.error);
      const next = result.data.items.find(
        (entry) =>
          entry.entityId !== batchId &&
          entry.batchStatus !== undefined &&
          entry.batchStatus.cardCount > 0 &&
          entry.batchStatus.preliminaryImageReviewComplete !== true,
      );
      if (next !== undefined) {
        clearCorrectionPreview();
        setLoadingBatch(true);
        setRotations(EMPTY_REVIEW_IMAGE_ROTATIONS);
        setImageIssue(false);
        setNotice(`New batch started: ${next.entityId.replace(/^tcvs:batch:/, '')}`);
        navigate(`/batches/${encodeURIComponent(next.entityId)}/images`, { replace: true });
        return;
      }
      page = result.data.nextPageToken;
    } while (page !== undefined);

    setRotations(EMPTY_REVIEW_IMAGE_ROTATIONS);
    setNotice('Image review saved. No other batches currently need image review.');
  }, [batchId, clearCorrectionPreview, navigate, storage]);

  const persistCorrections = async (card: CatalogListCardRecord) => {
    const pendingSides = (['front', 'back'] as const).filter((side) => rotations[side] !== 0);
    if (pendingSides.length === 0) return;
    const fresh = await catalog.get(card.cardId);
    if (!fresh.ok) throw new Error(fresh.message ?? fresh.error);
    try {
      for (const side of pendingSides) {
        setBusySide(side);
        const sourceUrl = photoReview.required
          ? active?.imageUrls?.[side]
          : fresh.data.imageUrls?.[side];
        if (sourceUrl === undefined) throw new Error(`${side}_image_unavailable`);
        const blob = await rotateReviewImageToJpeg(sourceUrl, rotations[side]);
        const file = new File([blob], `${card.cardId}-${side}-corrected.jpg`, {
          type: 'image/jpeg',
        });
        const upload = await catalog.createImageReplacementUpload(card.cardId, side, file);
        if (!upload.ok) throw new Error(upload.message ?? upload.error);
        const form = new FormData();
        for (const [key, value] of Object.entries(upload.data.uploadUrl.fields)) {
          form.append(key, value);
        }
        form.append('file', file);
        const response = await fetch(upload.data.uploadUrl.url, { method: 'POST', body: form });
        if (!response.ok) throw new Error(`upload_failed_${response.status}`);
        const replaced = await catalog.replaceImage(card.cardId, side, upload.data.s3Key);
        if (!replaced.ok) throw new Error(replaced.message ?? replaced.error);
        showCorrectionPreview(card.cardId, side, blob);
      }
    } finally {
      setBusySide(null);
    }
  };

  const saveAndNext = async () => {
    if (active === null || saving || loadingBatch || busySide !== null || !photoReview.ready)
      return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const correctionsPending = rotations.front !== 0 || rotations.back !== 0;
      const nextCardId = cards?.[activeIndex + 1]?.cardId ?? null;
      await persistCorrections(active);
      if (correctionsPending && photoReview.required) {
        clearCorrectionPreview();
        setRotations(EMPTY_REVIEW_IMAGE_ROTATIONS);
        photoReview.reload();
        await refresh();
        setNotice('Corrections saved. Review the updated images before approving.');
        return;
      }
      const result = await catalog.updatePreliminaryImageReview(
        active.cardId,
        imageIssue ? 'ISSUE' : 'APPROVED',
        ...(photoReview.reviewId === undefined ? [] : [photoReview.reviewId]),
      );
      if (!result.ok) throw new Error(result.message ?? result.error);
      setCards(
        (previous) =>
          previous?.map((card) =>
            card.cardId === active.cardId
              ? { ...card, preliminaryImageReview: result.data.preliminaryImageReview }
              : card,
          ) ?? previous,
      );
      if (correctionsPending && !(await refresh())) return;
      if (nextCardId !== null) {
        clearCorrectionPreview();
        setActiveId(nextCardId);
      } else {
        await moveToNextBatch();
      }
    } catch (saveError) {
      if (photoReview.required) photoReview.reload();
      setError(saveError instanceof Error ? saveError.message : 'image_review_save_failed');
    } finally {
      setSaving(false);
    }
  };

  if (batchId === '') return <p className="tcvs-error">Batch id is required.</p>;

  return (
    <div className="tcvs-image-only-review">
      <header className="tcvs-image-only-review-header">
        <div className="tcvs-image-only-review-heading">
          <Link
            to={`/batches/${encodeURIComponent(batchId)}/identify${
              active === null ? '' : `?card=${encodeURIComponent(active.cardId)}`
            }`}
            className="tcvs-linklike"
          >
            Back to Batch Identification
          </Link>
          <h2>Image Review</h2>
          {active === null ? null : (
            <>
              <span className="tcvs-mono">{active.shortCode ?? active.cardId}</span>{' '}
              <OwnershipLabel label={ownership[active.cardId]} />
            </>
          )}
        </div>
        <div className="tcvs-image-only-review-nav">
          <button
            type="button"
            aria-label="Previous card"
            disabled={activeIndex <= 0 || saving || loadingBatch || busySide !== null}
            onClick={() => moveTo(activeIndex - 1)}
          >
            &lt;
          </button>
          <span>
            {active === null ? 'No cards' : `Card ${activeIndex + 1} of ${cards?.length ?? 0}`}
            <small>
              {reviewedCount}/{cards?.length ?? 0} reviewed
            </small>
          </span>
          <button
            type="button"
            aria-label="Next card"
            disabled={
              active === null ||
              activeIndex >= (cards?.length ?? 0) - 1 ||
              saving ||
              loadingBatch ||
              busySide !== null
            }
            onClick={() => moveTo(activeIndex + 1)}
          >
            &gt;
          </button>
        </div>
      </header>

      <div className="tcvs-image-only-review-feedback" aria-live="polite">
        {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
        {error === null && notice !== null ? <p className="tcvs-notice">{notice}</p> : null}
      </div>

      {cards === null ? (
        <p>Loading cards…</p>
      ) : active === null ? (
        <p className="tcvs-muted">This batch has no cards to review.</p>
      ) : (
        <div className="tcvs-image-only-review-body" aria-busy={loadingBatch}>
          <aside className="tcvs-image-only-review-list" aria-label="Cards in batch">
            <div className="tcvs-image-only-review-list-heading">
              <strong>Cards in batch</strong>
              <span className="tcvs-muted">{cards.length}</span>
            </div>
            <div className="tcvs-image-only-review-card-list">
              {cards.map((card, index) => {
                const disposition = imageReviewDisposition(card);
                const frontUrl =
                  correctionPreview?.cardId === card.cardId
                    ? (correctionPreview.urls.front ?? card.imageUrls?.front)
                    : card.imageUrls?.front;
                return (
                  <button
                    key={card.cardId}
                    type="button"
                    className="tcvs-image-only-review-card"
                    data-active={active.cardId === card.cardId ? 'true' : 'false'}
                    data-status={disposition}
                    aria-current={active.cardId === card.cardId ? 'true' : undefined}
                    disabled={saving || loadingBatch || busySide !== null}
                    onClick={() => moveTo(index)}
                  >
                    {frontUrl === undefined ? (
                      <span className="tcvs-workbench-thumb tcvs-workbench-thumb-empty" />
                    ) : (
                      <img src={frontUrl} alt="" className="tcvs-workbench-thumb" />
                    )}
                    <span className="tcvs-image-only-review-card-summary">
                      <strong className="tcvs-mono">{card.shortCode ?? card.cardId}</strong>
                      <OwnershipLabel label={ownership[card.cardId]} />
                      <span className="tcvs-image-only-review-card-status">
                        {imageReviewDispositionLabel(disposition)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <main className="tcvs-image-only-review-inspector">
            <VersionedImageReviewStatus {...photoReview} />
            <BatchImageReviewControls
              active={active}
              rotations={rotations}
              grid={grid}
              imageIssue={imageIssue}
              saving={saving}
              loadingBatch={loadingBatch}
              reviewReady={photoReview.ready}
              busySide={busySide}
              correctionPreview={correctionPreview}
              onGridChange={setGrid}
              onImageIssueChange={setImageIssue}
              onRotationChange={(side, degrees) =>
                setRotations((current) => ({ ...current, [side]: degrees }))
              }
              onReset={() => setRotations(EMPTY_REVIEW_IMAGE_ROTATIONS)}
              onSave={() => void saveAndNext()}
            />
          </main>
        </div>
      )}
    </div>
  );
}

type BatchImageReviewControlsProps = {
  active: CatalogListCardRecord;
  rotations: ReviewImageRotations;
  grid: boolean;
  imageIssue: boolean;
  saving: boolean;
  loadingBatch: boolean;
  reviewReady?: boolean;
  busySide: ReviewImageSide | null;
  correctionPreview: CorrectionPreview | null;
  onGridChange: (value: boolean) => void;
  onImageIssueChange: (value: boolean) => void;
  onRotationChange: (side: ReviewImageSide, degrees: number) => void;
  onReset: () => void;
  onSave: () => void;
};

function BatchImageReviewControls({
  active,
  rotations,
  grid,
  imageIssue,
  saving,
  loadingBatch,
  reviewReady = true,
  busySide,
  correctionPreview,
  onGridChange,
  onImageIssueChange,
  onRotationChange,
  onReset,
  onSave,
}: BatchImageReviewControlsProps): JSX.Element {
  const busy = saving || loadingBatch || busySide !== null || !reviewReady;
  const resizableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = resizableRef.current;
    if (element === null) return;
    restoreImageReviewWidth(element);
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) persistImageReviewWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="tcvs-image-only-review-options">
        <ReviewImageGridAction
          alignmentGrid={grid}
          busy={busy}
          onAlignmentGridChange={onGridChange}
        />
        <span
          className="tcvs-image-only-review-status"
          data-status={imageReviewDisposition(active)}
          role="status"
        >
          {preliminaryImageReviewLabel(active)}
        </span>
        <label className="tcvs-image-issue-toggle">
          <input
            type="checkbox"
            checked={imageIssue}
            disabled={busy}
            onChange={(event) => onImageIssueChange(event.currentTarget.checked)}
          />
          <span>Image issue</span>
        </label>
      </div>

      <div className="tcvs-image-only-review-stage">
        <div
          ref={resizableRef}
          className="tcvs-image-only-review-resizable"
          aria-label="Resizable front and back image area"
        >
          <div className="tcvs-image-only-review-grid">
            {(['front', 'back'] as const).map((side) => {
              const url =
                correctionPreview?.cardId === active.cardId
                  ? (correctionPreview.urls[side] ?? active.imageUrls?.[side])
                  : active.imageUrls?.[side];
              return (
                <section key={side}>
                  <h3>{side === 'front' ? 'Front' : 'Back'}</h3>
                  {url === undefined ? (
                    <p className="tcvs-muted">Image unavailable</p>
                  ) : (
                    <ReviewImageRotationEditor
                      side={side}
                      rotationDegrees={rotations[side]}
                      alignmentGrid={grid}
                      busySide={busySide}
                      pairReset={
                        side === 'back'
                          ? {
                              disabled: rotations.front === 0 && rotations.back === 0,
                              onReset,
                            }
                          : undefined
                      }
                      onRotationChange={onRotationChange}
                    >
                      {(degrees, alignmentGrid) => (
                        <MagnifiableCardImage
                          src={url}
                          alt={`${side} card`}
                          imageClassName="tcvs-workbench-image-main"
                          rotationDegrees={degrees}
                          alignmentGrid={alignmentGrid}
                        />
                      )}
                    </ReviewImageRotationEditor>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        <footer className="tcvs-image-only-review-actions">
          <button type="button" disabled={busy} onClick={onSave}>
            {saving || loadingBatch
              ? 'Saving…'
              : imageIssue
                ? 'Save issue and next card'
                : 'Approve and next card'}
          </button>
        </footer>
      </div>
    </>
  );
}
